import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { NoveltyCheckTaskStatusService } from '../task-status-service';
import { GetTaskDetail, type ApiGetTaskDetailResponse } from '@/infrastructure/api-functions/core-drafting-api/get-task-detail-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { Emitter, isPsHttpError, LocationService, Logger, type Event } from '@patsnap/tahiti-core';
import type { NoveltyCheckTaskStatus } from '../../types';
import { NoveltyCheckTaskStep } from '../../enums';
import { NoveltyCheckTaskStatusModel } from '../../models/novelty-check-task-status-model';
import { TaskStatus } from '@/domains/task/enums';
import { TaskDateFormatter } from '@/services/date-format/task-date-formatter';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { CurrentTaskInputModel } from '@/domains/task/models/current-task-input-model';
import { I18nService } from '@telecom/shared/services/i18n';
import { DATA_VERSION_EXPIRED_CODE } from '../../constants';

declare module '@patsnap/tahiti-core' {
    interface LocationService {
        href: string;
    }
}

@injectable()
export class TaskStatusServiceImpl implements NoveltyCheckTaskStatusService {

    // eslint-disable-next-line max-params
    constructor(
        @inject(GetTaskDetail)
        private readonly _getTaskDetail: GetTaskDetail<string>,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(NoveltyCheckTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: NoveltyCheckTaskStatusModel,
        @inject(TaskDateFormatter)
        private readonly _taskDateFormatter: TaskDateFormatter,
        @inject(LocationService)
        private readonly _locationService: LocationService,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
        @inject(CurrentTaskInputModel)
        private readonly _currentTaskInputModel: CurrentTaskInputModel,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) { }

    private _serverSideTaskStep: NoveltyCheckTaskStep = NoveltyCheckTaskStep.INITIAL;

    get serverSideTaskStep(): NoveltyCheckTaskStep {
        return this._serverSideTaskStep;
    }

    private _onServerSideTaskStepEmitter = new Emitter<NoveltyCheckTaskStep>();

    get onServerSideTaskStep(): Event<NoveltyCheckTaskStep> {
        return this._onServerSideTaskStepEmitter.event;
    }

    async pullTaskStatus(): Promise<NoveltyCheckTaskStatus> {
        const taskId = this._currentTaskService.taskId;
        this._logger.info('获取任务状态', taskId);
        const data = await this._getTaskStatusRepository(taskId);
        this._noveltyCheckTaskStatusModel.update(() => data);
        this._currentTaskInputModel.update((state) => {
            state.inputLanguage = data.inputLanguage;
            return state;
        });
        this._retrievalResultReportModel.update((state) => {
            state.userInput = data.userInput;
            state.translationInput = data.translationInput;
            state.techEssential = state.techEssential|| data.translationInput || data.userInput;
            state.imageToTextResult = data.imageToTextResult;
            return state;
        });
        this._logger.info('获取任务状态成功', taskId, data);
        return Promise.resolve(data);
    }

    private async _getTaskStatusRepository(taskId: string): Promise<NoveltyCheckTaskStatus> {
        const mockDataVersionExpiredTaskStatus = 'DATA_VERSION_EXPIRED';
        const response = await this._getTaskDetail.getTaskDetail(taskId)
            .catch((error) => {
                if (isPsHttpError(error) && error.cause && error.cause.error_code === DATA_VERSION_EXPIRED_CODE) {
                    return {
                        task_status: mockDataVersionExpiredTaskStatus,
                    } as const;
                }
                throw error;
            });
        if (response.task_status === mockDataVersionExpiredTaskStatus) {
            return {
                taskStatus: TaskStatus.DATA_VERSION_EXPIRED,
                taskStep: NoveltyCheckTaskStep.INITIAL,
                lastUpdateTime: '',
                userInput: '',
                inputLanguage: '',
                translationInput: undefined,
            };
        }
        const taskStep = this._getStepFromUrl() || this._convertTaskStep(response.current_step);
        this._serverSideTaskStep = taskStep;
        this._onServerSideTaskStepEmitter.fire(taskStep);
        return {
            taskStatus: response.task_status,
            taskStep,
            lastUpdateTime: this._taskDateFormatter.format(response.updated_at),
            userInput: response.user_input,
            inputLanguage: this._getInputLanguage(response),
            translationInput: response.translation_input,
            imageToTextResult: response.image_to_text_result,
        };
    }

    private _getInputLanguage(response: ApiGetTaskDetailResponse<string>): string {
        if (response.input_lang) {
            return response.input_lang.toUpperCase();
        }
        return this._i18nService.locale.value.toUpperCase();
    }

    /**
     * 从 url 获取 ?currentStep={step} 中获取 step 的值
     */
    private _getStepFromUrl(): NoveltyCheckTaskStep | undefined {
        const url = this._locationService.href;
        const searchParams = new URLSearchParams(url.split('?')[1]);
        const step = searchParams.get('currentStep');
        try {
            const taskStep = this._convertTaskStep(step!);
            this._logger.info('从 url 获取 step 成功', taskStep);
            return taskStep;
        }
        catch (error) {
            this._logger.warn('从 url 获取 step 失败', error);
            return undefined;
        }
    }

    // eslint-disable-next-line complexity
    private _convertTaskStep(step: string): NoveltyCheckTaskStep {
        if (step === '') {
            return NoveltyCheckTaskStep.INITIAL;
        }
        else if (step === 'NOVELTY_SEARCH_INPUT') {
            return NoveltyCheckTaskStep.SUMMARY;
        }
        else if (step === 'NOVELTY_SEARCH_TECH_FEATURE') {
            return NoveltyCheckTaskStep.NOVELTY_SEARCH_TECH_FEATURE;
        }
        else if (step === 'NOVELTY_SEARCH_RETRIEVAL_ELEMENTS') {
            return NoveltyCheckTaskStep.NOVELTY_SEARCH_RETRIEVAL_ELEMENTS;
        }
        else if (step === 'CONFIRM') {
            return NoveltyCheckTaskStep.SEARCH_FINISHED;
        }
        else if (step === 'PATENT_CONFIRM') {
            return NoveltyCheckTaskStep.PATENT_CONFIRM;
        }
        else if (step === 'GENERATE_FEATURE_COMPARE_TABLE') {
            return NoveltyCheckTaskStep.GENERATE_FEATURE_COMPARE_TABLE;
        }
        else if (step === 'RESULT') {
            return NoveltyCheckTaskStep.REPORT_CREATED;
        }
        throw new Error(`未知任务步骤: ${step}`);
    }
}
